import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronUp, Plus, X, StickyNote as StickyNoteIcon, Edit2, Palette, Clock, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { useStickyNotes } from "@/hooks/useStickyNotes";
import { useReminders } from "@/hooks/useReminders";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const NOTE_COLORS = [
  "#fbbf24", // yellow
  "#fb7185", // pink
  "#34d399", // green
  "#60a5fa", // blue
  "#a78bfa", // purple
  "#f97316", // orange
  "#10b981", // emerald
  "#3b82f6", // blue
];

export function StickyNotes() {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'notes' | 'reminders' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingReminderId, setEditingReminderId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editReminderTitle, setEditReminderTitle] = useState("");
  const [editReminderContent, setEditReminderContent] = useState("");
  const [editReminderDate, setEditReminderDate] = useState<Date | undefined>(undefined);
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const { notes, createNote, updateNote, deleteNote } = useStickyNotes();
  const { reminders, createReminder, updateReminder, deleteReminder, getDueAndOverdueCount } = useReminders();

  // Don't render sticky notes if user is not authenticated, on homepage, on forms screen, auth page, onboarding page, signup success page, quinn page, or on mobile
  if (!user || location.pathname === '/' || location.pathname.includes('/forms') || location.pathname === '/auth' || location.pathname === '/onboarding' || location.pathname === '/signupsuccess' || location.pathname === '/dashboard/quinn') {
    return null;
  }

  // Import mobile hook
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  // Don't render on mobile devices
  if (isMobile) {
    return null;
  }

  const handleCreateNote = async () => {
    const newNote = await createNote();
    if (newNote) {
      setEditingId(newNote.id);
      setEditTitle(newNote.title);
      setEditContent(newNote.content);
    }
  };

  const handleStartEdit = (note: typeof notes[0]) => {
    setEditingId(note.id);
    setEditTitle(note.title);
    setEditContent(note.content);
  };

  const handleSaveEdit = async () => {
    if (editingId) {
      await updateNote(editingId, { 
        title: editTitle.trim() || "Untitled Note", 
        content: editContent 
      });
      setEditingId(null);
      setEditTitle("");
      setEditContent("");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditContent("");
  };

  const handleColorChange = async (noteId: string, color: string) => {
    await updateNote(noteId, { color });
    setShowColorPicker(null);
  };

  const handleStartEditReminder = (reminder: typeof reminders[0]) => {
    setEditingReminderId(reminder.id);
    setEditReminderTitle(reminder.title);
    setEditReminderContent(reminder.content);
    setEditReminderDate(reminder.due_date ? new Date(reminder.due_date) : undefined);
  };

  const handleSaveEditReminder = async () => {
    if (editingReminderId) {
      await updateReminder(editingReminderId, { 
        title: editReminderTitle.trim() || "Untitled Reminder", 
        content: editReminderContent,
        due_date: editReminderDate?.toISOString() || null
      });
      setEditingReminderId(null);
      setEditReminderTitle("");
      setEditReminderContent("");
      setEditReminderDate(undefined);
    }
  };

  const handleCancelEditReminder = () => {
    setEditingReminderId(null);
    setEditReminderTitle("");
    setEditReminderContent("");
    setEditReminderDate(undefined);
  };

  const dueCount = getDueAndOverdueCount();

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Toggle Buttons */}
      <div className="flex justify-center">
        <div className="flex items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveTab(activeTab === 'notes' ? null : 'notes')}
            className={cn(
              "rounded-t-lg rounded-b-none border-b-0 bg-background shadow-lg transition-all duration-200 rounded-r-none",
              activeTab === 'notes' && "bg-muted"
            )}
          >
            <StickyNoteIcon className="mr-2 h-4 w-4" />
            Sticky Notes
            {notes.length > 0 && (
              <span className="ml-1 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5">
                {notes.length}
              </span>
            )}
            <ChevronUp
              className={cn(
                "ml-2 h-4 w-4 transition-transform duration-200",
                activeTab === 'notes' && "rotate-180"
              )} 
            />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveTab(activeTab === 'reminders' ? null : 'reminders')}
            className={cn(
              "rounded-t-lg rounded-b-none border-b-0 bg-background shadow-lg transition-all duration-200 rounded-l-none border-l-0",
              activeTab === 'reminders' && "bg-muted"
            )}
          >
            <Clock className="mr-2 h-4 w-4" />
            Reminders
            {reminders.length > 0 && (
              <span className="ml-1 bg-purple-500 text-white text-xs rounded-full px-1.5 py-0.5">
                {reminders.length}
              </span>
            )}
            {dueCount > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                {dueCount}
              </span>
            )}
            <ChevronUp 
              className={cn(
                "ml-2 h-4 w-4 transition-transform duration-200",
                activeTab === 'reminders' && "rotate-180"
              )} 
            />
          </Button>
        </div>
      </div>

      {/* Sticky Notes Bar */}
      <div className={cn(
        "bg-background border-t shadow-lg transition-all duration-300 ease-in-out",
        activeTab === 'notes' ? "h-64" : "h-0 overflow-hidden"
      )}>
        <div className="h-full overflow-y-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Sticky Notes</h3>
            <Button
              size="sm"
              onClick={handleCreateNote}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Note
            </Button>
          </div>
          <div className="flex gap-4 min-h-0 flex-1">
            {/* Existing Notes */}
            {notes.map((note) => (
              <div key={note.id} className="flex-shrink-0 w-64">
                <div 
                  className="p-3 rounded-lg border h-full relative group"
                  style={{ 
                    backgroundColor: `${note.color}20`,
                    borderColor: `${note.color}40`
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    {editingId === note.id ? (
                      <Input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="text-sm font-medium flex-1 mr-2 h-6 p-1"
                        style={{ color: note.color }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveEdit();
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                      />
                    ) : (
                      <span 
                        className="text-sm font-medium flex-1 cursor-pointer"
                        style={{ color: note.color }}
                        onClick={() => handleStartEdit(note)}
                      >
                        {note.title}
                      </span>
                    )}
                    
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowColorPicker(showColorPicker === note.id ? null : note.id)}
                        className="h-6 w-6 p-0 hover:bg-black/10"
                        title="Change color"
                      >
                        <Palette className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteNote(note.id)}
                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20"
                        title="Delete note"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Color Picker */}
                  {showColorPicker === note.id && (
                    <div className="absolute top-8 right-0 bg-background border rounded-lg p-2 shadow-lg z-10 flex gap-1">
                      {NOTE_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => handleColorChange(note.id, color)}
                          className="w-6 h-6 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          title={`Change to ${color}`}
                        />
                      ))}
                    </div>
                  )}
                  
                  {editingId === note.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        placeholder="Write your note..."
                        className="min-h-[80px] text-sm bg-transparent resize-none"
                        style={{ borderColor: `${note.color}40` }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            handleSaveEdit();
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <div className="flex gap-1">
                        <Button size="sm" onClick={handleSaveEdit} className="flex-1">
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancelEdit} className="flex-1">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="text-sm whitespace-pre-wrap cursor-pointer hover:bg-black/5 rounded p-1 -m-1 transition-colors min-h-[80px]"
                      onClick={() => handleStartEdit(note)}
                      style={{ color: `${note.color}dd` }}
                    >
                      {note.content || (
                        <span className="text-muted-foreground italic">
                          Click to add content...
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="text-xs text-muted-foreground mt-2">
                    {new Date(note.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {notes.length === 0 && (
              <div className="flex items-center justify-center w-full h-full text-muted-foreground">
                <div className="text-center">
                  <StickyNoteIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No sticky notes yet</p>
                  <p className="text-xs">Click the + button to create your first note</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reminders Bar */}
      <div className={cn(
        "bg-background border-t shadow-lg transition-all duration-300 ease-in-out",
        activeTab === 'reminders' ? "h-64" : "h-0 overflow-hidden"
      )}>
        <div className="h-full overflow-y-auto p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Reminders</h3>
            <Button
              size="sm"
              onClick={() => createReminder()}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Reminder
            </Button>
          </div>
          <div className="flex gap-4 min-h-0 flex-1">
            {/* Existing Reminders */}
            {reminders.map((reminder) => {
              const isOverdue = reminder.due_date && new Date(reminder.due_date) < new Date() && !reminder.is_completed;
              const isDueToday = reminder.due_date && new Date(reminder.due_date).toDateString() === new Date().toDateString();
              
              return (
                <div key={reminder.id} className="flex-shrink-0 w-64">
                  <div 
                    className="p-3 rounded-lg border h-full relative group bg-gray-50 border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      {editingReminderId === reminder.id ? (
                        <Input
                          value={editReminderTitle}
                          onChange={(e) => setEditReminderTitle(e.target.value)}
                          className="text-sm font-medium flex-1 mr-2 h-6 p-1 text-gray-700"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveEditReminder();
                            } else if (e.key === 'Escape') {
                              handleCancelEditReminder();
                            }
                          }}
                        />
                      ) : (
                        <span 
                          className="text-sm font-medium flex-1 cursor-pointer text-gray-700"
                          onClick={() => handleStartEditReminder(reminder)}
                        >
                          {reminder.title}
                        </span>
                      )}
                      
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteReminder(reminder.id)}
                          className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/20"
                          title="Delete reminder"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    
                    {editingReminderId === reminder.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editReminderContent}
                          onChange={(e) => setEditReminderContent(e.target.value)}
                          placeholder="Write your reminder..."
                          className="min-h-[60px] text-sm bg-transparent resize-none border-gray-300"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.ctrlKey) {
                              handleSaveEditReminder();
                            } else if (e.key === 'Escape') {
                              handleCancelEditReminder();
                            }
                          }}
                        />
                        
                        {/* Date Picker */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !editReminderDate && "text-muted-foreground"
                              )}
                              size="sm"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {editReminderDate ? format(editReminderDate, "PPP") : <span>Pick a due date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-[60]" align="start">
                            <Calendar
                              mode="single"
                              selected={editReminderDate}
                              onSelect={setEditReminderDate}
                              initialFocus
                              className="p-3 pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                        
                        <div className="flex gap-1">
                          <Button size="sm" onClick={handleSaveEditReminder} className="flex-1">
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleCancelEditReminder} className="flex-1">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "text-sm whitespace-pre-wrap min-h-[60px] p-1 -m-1 rounded transition-colors cursor-pointer hover:bg-gray-100 text-gray-600",
                          reminder.is_completed && "line-through opacity-50"
                        )}
                        onClick={() => handleStartEditReminder(reminder)}
                      >
                        {reminder.content || (
                          <span className="text-muted-foreground italic">
                            Click to add content...
                          </span>
                        )}
                      </div>
                    )}
                    
                    {reminder.due_date && (
                      <div className={cn(
                        "text-xs mt-2 px-2 py-1 rounded",
                        isOverdue ? "bg-red-100 text-red-700" :
                        isDueToday ? "bg-yellow-100 text-yellow-700" :
                        "bg-gray-100 text-gray-600"
                      )}>
                        Due: {new Date(reminder.due_date).toLocaleDateString()}
                        {isOverdue && " (Overdue)"}
                        {isDueToday && " (Today)"}
                      </div>
                    )}
                    
                    <div className="text-xs text-muted-foreground mt-2">
                      {new Date(reminder.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Empty State */}
            {reminders.length === 0 && (
              <div className="flex items-center justify-center w-full h-full text-muted-foreground">
                <div className="text-center">
                  <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No reminders yet</p>
                  <p className="text-xs">Click the + button to create your first reminder</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
